import React, { useCallback, useEffect, useRef, useState } from 'react';
import Popup from 'reactjs-popup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { ReactComponent as IconClose } from './../../assets/img/icon/icon-close.svg';
import { lockBodyScroll, unlockBodyScroll } from './../../utils/scrollLock';
import { fetchAdvisorConsent, updateAdvisorConsent, loadPartnerRole, saveAdvisor } from './../../services/advisorState';
import './advisorConsent.scss';

// BE cuma ngizinin Owner/Admin (role O/A) ngubah consent. Role gak dikenal (userData kosong dsb.)
// toggle-nya tetap aktif — BE yang mutusin, 403-nya ditangani di toggleConsent.
const READ_ONLY_ROLES = ['Staff'];
const FORBIDDEN = 'FeatureUnauthorizedForbidden';

// Modal "My Advisor" (tombol Details di sidebar, SKAI1-1457). Toggle-nya langsung nyimpen, gak ada
// tombol Save — makanya gak pernah digeser duluan: yang tampil selalu nilai yang udah tersimpan di BE.
const PopAdvisorConsent = ({ open, onClose, advisorName }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [consent, setConsent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  // Nomor request terakhir. Response yang telat (modal udah ditutup / udah ada request baru) dibuang.
  const requestId = useRef(0);

  const loadConsent = useCallback(async ({ silent = false } = {}) => {
    const id = ++requestId.current;
    if(!silent) setStatus('loading');

    const result = await fetchAdvisorConsent();
    if(id !== requestId.current) return;

    if(result.ok){
      // Nama advisor di sidebar ikut disegerin — sesudah cek response basi, biar response telat
      // (mis. abis logout) gak nulis ulang nama akun sebelumnya.
      saveAdvisor(result.name);
      setConsent(result.consent);
      setStatus('ready');
    }else if(!silent){
      setConsent(null);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if(!open) return undefined;

    setSaving(false);
    setSaveError('');
    setReadOnly(READ_ONLY_ROLES.includes(loadPartnerRole()));
    loadConsent();
    lockBodyScroll();

    return () => {
      requestId.current += 1;
      unlockBodyScroll();
    };
  }, [open, loadConsent]);

  const toggleConsent = async () => {
    if(!consent || saving || readOnly) return;

    const id = ++requestId.current;
    setSaving(true);
    setSaveError('');

    const result = await updateAdvisorConsent(!consent.enabled);
    if(id !== requestId.current) return;
    setSaving(false);

    if(result.ok){
      setConsent(result.consent);
      return;
    }

    const forbidden = result.code === FORBIDDEN;
    const message = forbidden ? t('advisor.consent.noPermission') : t('advisor.consent.saveFailed');
    if(forbidden) setReadOnly(true);
    setSaveError(message);
    // Network/timeout (tanpa `code`) udah di-toast global sama interceptor — jangan dobel, dan jangan
    // GET ulang (bakal gagal juga & ke-toast lagi). Nilai lama tetap tampil; klik lagi ngirim nilai
    // absolut yang sama, jadi aman walau PUT tadi ternyata udah ke-commit (BE anggap no-op).
    if(!result.code) return;
    toast.error(<div className="toast-con"><div>Oppsss!</div>{message}</div>);
    // BE sempet jawab error → sinkron ulang diam-diam biar UI ikut nilai yang beneran tersimpan.
    loadConsent({ silent: true });
  };

  const name = typeof advisorName === 'string' ? advisorName.trim() : '';
  let control = null;
  let note = null;

  if(status === 'loading'){
    control = <ul className="loading" aria-hidden="true"><li></li><li></li><li></li></ul>;
    note = <p className="advisor-consent-note" role="status">{t('advisor.consent.loading')}</p>;
  }else if(status === 'error'){
    note = (
      <p className="advisor-consent-note is-error" role="alert">
        {t('advisor.consent.loadFailed')}{' '}
        <button type="button" className="btn btn-link" onClick={() => loadConsent()}>{t('advisor.consent.retry')}</button>
      </p>
    );
  }else if(!consent){
    note = <p className="advisor-consent-note" role="status">{t('advisor.consent.unavailable')}</p>;
  }else{
    // Sengaja aria-disabled, BUKAN `disabled`: tombol yang lagi fokus terus di-disable bikin fokus
    // keyboard loncat ke <body>. Klik pas saving/read-only udah ditolak di toggleConsent.
    control = (
      <button
        type="button"
        role="switch"
        className={`advisor-switch${consent.enabled ? ' is-on' : ''}${saving ? ' is-saving' : ''}`}
        aria-checked={consent.enabled}
        aria-labelledby="advisor-consent-label"
        aria-busy={saving}
        aria-disabled={saving || readOnly}
        onClick={toggleConsent}
      >
        <span className="advisor-switch-track"></span>
        <span className="advisor-switch-knob"></span>
      </button>
    );

    if(saveError){
      note = <p className="advisor-consent-note is-error" role="alert">{saveError}</p>;
    }else if(saving){
      note = <p className="advisor-consent-note" role="status">{t('advisor.consent.saving')}</p>;
    }else if(readOnly){
      note = <p className="advisor-consent-note">{t('advisor.consent.noPermission')}</p>;
    }
  }

  // X-only (SKAI1-1482): klik overlay & Escape gak nutup modal, sama kayak popup MSF. Tombol X
  // sendiri di-disable selama nyimpen.
  return (
    <Popup open={open} onClose={onClose} className="pop-advisor" closeOnDocumentClick={false} closeOnEscape={false}>
      <div className="pop-head">
        <span>{t('advisor.consent.title')}</span>
        <button type="button" onClick={onClose} disabled={saving} aria-label={t('advisor.close')}>
          <IconClose />
        </button>
      </div>

      <div className="pop-section">
        <div className="advisor-consent-text">
          <p>{name ? t('advisor.consent.intro', { name }) : t('advisor.consent.introNoName')}</p>
        </div>

        <div className="advisor-consent-box">
          <span id="advisor-consent-label">
            {t('advisor.consent.toggleLabel')}
            {status === 'ready' && consent && <> {t(consent.enabled ? 'advisor.consent.on' : 'advisor.consent.off')}</>}
          </span>
          {control}
        </div>
        <p className="advisor-consent-disclaimer">{t('advisor.consent.disclaimer')}</p>
        {note}
      </div>
    </Popup>
  );
};

export default PopAdvisorConsent;
